import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { Field } from '../../UI';
import { formatCpfCnpj } from '../../../../helpers/utils/utils';

/**
 * Dados cadastrais da aba Devedor (AW2-1134 Etapas 1-2) — porta de
 * `IdentificacaoDevedor.jsx` real, mesmo agrupamento visual em 3 linhas
 * (Categoria/CPF-CNPJ/Data Fundação/Situação Empresa; Código no
 * Cliente/Código do Devedor/Razão Social/Preposto; Nome Fantasia/Inscrição
 * Estadual).
 *
 * Lê sempre `formFields` (estado local de `AbaDevedor.jsx`, mantido em
 * sincronia com `dadosDevedor` via `devedorController.mapearDevedorParaFormFields`),
 * nunca `dadosDevedor` diretamente — mesma arquitetura da tela real
 * (`formFields` como única fonte de verdade de exibição, sincronizado por
 * efeito). Campos editáveis usam `disabled={!podeModificarFase || !isEditMode}`
 * (AC1: "todos os campos... editáveis, sem exceções de campos travados").
 * 3 dos 10 campos desta tela ficam SEMPRE somente-leitura, mesmo em modo
 * edição — Data Fundação e Situação Empresa (resultado da consulta externa
 * ao CNPJ/CPF) e Código do Devedor (identificador do recurso, nunca
 * enviado como edição); nenhum dos 3 tem contrapartida em
 * `devedorController.montarPayloadDevedor` (Etapa 9). Mais 2 campos fora
 * desta tela (Última Consulta/Retorno Consulta, `AbaDevedor.jsx`, Etapa 10)
 * têm a mesma natureza — 5 campos travados no total na aba inteira, todos
 * resultado de processo externo ou identificador, nenhum dado editável
 * pelo usuário. Divergência com o texto literal do AC1 ("sem exceções")
 * levantada no planejamento e CONFIRMADA EXPLICITAMENTE pelo usuário na
 * Etapa 11 (auditoria final) — manter travado, aceito como exceção
 * documentada (mesmo comportamento da produção real hoje). Ver
 * `docs/qa-aw2-1134-etapa-11.md`.
 *
 * `categoria` (string enum, ex. `"PESSOA_JURIDICA"`) é o campo correto para o
 * value do select "Categoria" — ver achado documentado em
 * `devedorController.js`/`src/prototipo/CLAUDE.md`.
 *
 * CPF/CNPJ: somente-leitura mostra via `formatCpfCnpj` (utilitário puro,
 * detecta CPF vs. CNPJ pelo tamanho); em modo edição mostra o valor CRU sem
 * máscara — aplicar `formatCpfCnpj` também durante a digitação corromperia o
 * valor a cada re-render (o texto formatado seria re-formatado de novo em
 * cima de si mesmo). Máscara de digitação ao vivo (CNPJ-alfanumérico vs.
 * CPF, por `categoria`) fica como débito técnico não implementado nesta
 * etapa — decisão registrada em "Padrões e Decisões" do plano: o `Field`
 * genérico (`components/UI/index.jsx`) só tem `tipo="documento"` (sempre
 * CNPJ-alfanumérico), sem modo CPF; estendê-lo tem blast radius maior
 * (componente compartilhado por todo o módulo) do que o escopo desta etapa
 * justifica.
 */
// Divergência deliberada da produção real (`Identificacao Devedor/IdentificacaoDevedor.jsx`,
// que formata incondicionalmente e mostra "0" quando não há `idDevedor`) —
// aqui um valor ausente mostra vazio, não "0" (correção, não descuido).
function formatCodigoDevedor(idDevedor) {
  return idDevedor != null && idDevedor !== '' ? Number(idDevedor).toLocaleString('pt-BR', { minimumFractionDigits: 0 }) : '';
}

function formatCnpjCpfExibido(cnpjCpf, isEditMode) {
  if (isEditMode) {
    return cnpjCpf ?? '';
  }
  return cnpjCpf ? formatCpfCnpj(cnpjCpf) : '';
}

export default function IdentificacaoDevedor({
  formFields, categoriasOptions, podeModificarFase, isEditMode, onChangeCampo, errors,
}) {
  const disabledCampoEditavel = !podeModificarFase || !isEditMode;
  const cnpjCpfExibido = formatCnpjCpfExibido(formFields?.cnpjCpf, isEditMode);

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr) minmax(0, 1fr)', md: 'repeat(4, minmax(0, 1fr))' },
          gap: 'clamp(10px, 0.8vw, 22px)',
        }}
      >
        <Field
          label="Categoria:"
          tipo="select"
          name="categoria"
          options={categoriasOptions}
          value={formFields?.categoria ?? ''}
          onChange={onChangeCampo}
          disabled={disabledCampoEditavel}
        />
        <Field
          label="CPF/CNPJ:"
          name="cnpjCpf"
          value={cnpjCpfExibido}
          onChange={onChangeCampo}
          disabled={disabledCampoEditavel}
          error={errors?.cnpjCpf?.error}
          helperText={errors?.cnpjCpf?.errorMessage}
        />
        <Field label="Data Fundação:" value={formFields?.dataFundacao ?? ''} disabled />
        <Field label="Situação Empresa:" value={formFields?.situacaoCnpj ?? ''} disabled />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr) minmax(0, 1fr)', md: 'repeat(4, minmax(0, 1fr))' },
          gap: 'clamp(10px, 0.8vw, 22px)',
          mt: 'clamp(10px, 0.8vw, 22px)',
        }}
      >
        <Field
          label="Código no Cliente:"
          name="codigoNoCliente"
          value={formFields?.codigoNoCliente ?? ''}
          onChange={onChangeCampo}
          disabled={disabledCampoEditavel}
          error={errors?.codigoNoCliente?.error}
          helperText={errors?.codigoNoCliente?.errorMessage}
        />
        <Field label="Código do Devedor:" value={formatCodigoDevedor(formFields?.idDevedor)} disabled />
        <Field
          label="Razão Social/Nome:"
          name="razaoSocial"
          value={formFields?.razaoSocial ?? ''}
          onChange={onChangeCampo}
          disabled={disabledCampoEditavel}
          error={errors?.razaoSocial?.error}
          helperText={errors?.razaoSocial?.errorMessage}
        />
        <Field
          label="Preposto:"
          name="preposto"
          value={formFields?.preposto ?? ''}
          onChange={onChangeCampo}
          disabled={disabledCampoEditavel}
          error={errors?.preposto?.error}
          helperText={errors?.preposto?.errorMessage}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.3fr) minmax(0, 1fr)' },
          gap: 'clamp(10px, 0.8vw, 22px)',
          mt: 'clamp(10px, 0.8vw, 22px)',
        }}
      >
        <Field
          label="Nome Fantasia:"
          name="nomeFantasia"
          value={formFields?.nomeFantasia ?? ''}
          onChange={onChangeCampo}
          disabled={disabledCampoEditavel}
          error={errors?.nomeFantasia?.error}
          helperText={errors?.nomeFantasia?.errorMessage}
        />
        <Field
          label="Inscrição Estadual:"
          name="inscricaoEstadual"
          value={formFields?.inscricaoEstadual ?? ''}
          onChange={onChangeCampo}
          disabled={disabledCampoEditavel}
          error={errors?.inscricaoEstadual?.error}
          helperText={errors?.inscricaoEstadual?.errorMessage}
        />
      </Box>
    </>
  );
}

IdentificacaoDevedor.propTypes = {
  formFields: PropTypes.shape({
    categoria: PropTypes.string,
    cnpjCpf: PropTypes.string,
    dataFundacao: PropTypes.string,
    situacaoCnpj: PropTypes.string,
    codigoNoCliente: PropTypes.string,
    idDevedor: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    razaoSocial: PropTypes.string,
    preposto: PropTypes.string,
    nomeFantasia: PropTypes.string,
    inscricaoEstadual: PropTypes.string,
  }),
  categoriasOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    value: PropTypes.string,
  })),
  podeModificarFase: PropTypes.bool,
  isEditMode: PropTypes.bool,
  onChangeCampo: PropTypes.func,
  errors: PropTypes.objectOf(PropTypes.shape({
    error: PropTypes.bool,
    errorMessage: PropTypes.string,
  })),
};

IdentificacaoDevedor.defaultProps = {
  formFields: null,
  categoriasOptions: [],
  errors: {},
  podeModificarFase: false,
  isEditMode: false,
  onChangeCampo: () => null,
};
